import { SIDE_TABLE_COLUMN_TYPES, tableSideColumnMapper } from "@loanbossinc/component-library";
import { defaultField, defaultBooleanField, mapRefSubtext } from "helpers/row-logic-helper";

import { shouldShowField } from "../loanDetailEditUtil";

import {
  shouldShowReplacementPer_Per,
  shouldShowRepairPerId_Per,
  deadlineMapNestedRows,
  shouldShowTaxInsuranceReserve,
  shouldShowTaxInsuranceReserveAccount,
  shouldShowReplacementReserve,
  shouldShowReplacementReserveAccount,
  shouldShowRepairReserve,
  shouldShowRepairReserveAccount,
  shouldShowRepairCompletionDeadline,
  shouldShowTilcReserves,
  shouldShowTilcReservesMisc,
  shouldShowGoodNewsMoneyDrawn,
  shouldShowFfeReserve,
  shouldShowFfeReserveAccount,
  shouldShowCapExReserves,
  shouldShowCapExReservesMisc,
  shouldShowCapExCompletionDeadline,
  shouldShowCapExReservesDrawn,
  shouldShowShortfallReserve,
  shouldShowShortfallReserveMisc,
  shouldShowOperatingAccount,
  shouldShowInterestRateCapReserve,
  shouldShowInterestRateCapReserveMisc,
  shouldShowInterestReserve,
  shouldShowInterestReserveMisc,
  shouldShowmInCashDeposit,
  shouldShowmMiscReserves
} from "./section-loan-escrow-reserve-rows-logic";

const DEFAULT_WIDTH = 138;

export const rows = {
  taxReserve: {
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Tax Reserve",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.BOOLEAN_SUBTEXT,
        map: data => {
          const taxReserve = defaultBooleanField(data, "covenants.taxReserve");
          if (taxReserve === "--") {
            return false;
          }

          return taxReserve;
        },
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.taxReserve"
      }
    ]
  },

  insuranceReserve: {
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Insurance Reserve",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.BOOLEAN_SUBTEXT,
        map: data => {
          const insuranceReserve = defaultBooleanField(data, "covenants.insuranceReserve");
          if (insuranceReserve === "--") {
            return false;
          }

          return insuranceReserve;
        },
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.insuranceReserve"
      }
    ]
  },
  taxInsuranceReserve: {
    isVisible: data => shouldShowTaxInsuranceReserve(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Tax and Insurance Reserve",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: data => {
          const item = defaultBooleanField(data, "covenants.taxInsuranceReserve");
          if (item === "--") {
            return false;
          }

          return item;
        },
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.taxInsuranceReserve"
      }
    ]
  },

  taxInsuranceReserveAccount: {
    isVisible: data => shouldShowTaxInsuranceReserveAccount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "tax and insurance reserve account",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => {
          const item = defaultBooleanField(data, "covenants.taxInsuranceReserveAccount");
          if (item === "--") {
            return false;
          }

          return item;
        },
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.taxInsuranceReserveAccount"
      }
    ]
  },
  replacementReserve: {
    isVisible: data => shouldShowReplacementReserve(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Replacement Reserve",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: data => defaultField(data, "covenants.replacementReserve"),
        suffix: data => {
          const suffix = defaultField(data, "covenants.replacementPer.name");
          const usePer = shouldShowReplacementPer_Per(data) ? "per" : "";

          return !suffix || suffix === "--" ? null : ` ${usePer} ${suffix}`;
        },
        Cell: tableSideColumnMapper,
        subtextMappers: [, { map: data => mapRefSubtext(data, "covenants.replacementReserveRef") }],
        colSpan: 2,
        decimals: 2
      }
    ]
  },

  replacementReserveAccount: {
    isVisible: data => shouldShowReplacementReserveAccount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Replacement Reserve Account",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.replacementReserveAccount"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.replacementReserveAccount"
      }
    ]
  },
  repairReserve: {
    isVisible: data => shouldShowRepairReserve(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Repair Reserve",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: data => defaultField(data, "covenants.repairReserve"),
        Cell: tableSideColumnMapper,
        suffix: data => {
          const suffix = defaultField(data, "covenants.repairPer.name");
          const usePer = shouldShowRepairPerId_Per(data) ? "per" : "";

          return !suffix || suffix === "--" ? null : ` ${usePer} ${suffix}`;
        },
        colSpan: 1,
        decimals: 2
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.repairReserve"
      }
    ]
  },

  repairReserveAccount: {
    isVisible: data => shouldShowRepairReserveAccount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Repair Reserve Account",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.repairReserveAccount"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.repairReserveAccount"
      }
    ]
  },
  repairCompletionDeadline: {
    isVisible: data => shouldShowRepairCompletionDeadline(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "REPAIR COMPLETION DEADLINE",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.STRING_SUBTEXT_NESTED,
        mapNestedRows: data => deadlineMapNestedRows(data, "repairCompletionDeadline"),
        map: row => defaultField(row, "name"),
        Cell: tableSideColumnMapper,
        subtextMappers: [, { map: row => mapRefSubtext(row, "dueDate") }],
        colSpan: 2
      }
    ]
  },

  tilcReserves: {
    isVisible: data => shouldShowTilcReserves(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "TILC Reserves",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: data => defaultField(data, "covenants.tilcReserves"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.tilcReserves"
      }
    ]
  },

  tilcReservesMisc: {
    isVisible: data => shouldShowTilcReservesMisc(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "TILC Reserves Misc",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.tilcReservesMisc"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.tilcReservesMisc"
      }
    ]
  },

  goodNewsMoneyDeadline: {
    isVisible: data => shouldShowField(data, "covenants.goodNewsMoneyDeadline"),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Good News Money Deadline",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: data => defaultField(data, "covenants.goodNewsMoneyDeadline"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: " ",
        Cell: tableSideColumnMapper
      }
    ]
  },

  goodNewsMoneyDrawn: {
    isVisible: data => shouldShowGoodNewsMoneyDrawn(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Good News Money Drawn",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: data => defaultField(data, "covenants.goodNewsMoneyDrawn"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.goodNewsMoneyDrawn"
      }
    ]
  },

  ffeReserve: {
    isVisible: data => shouldShowFfeReserve(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "FF&E Reserve",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: data => {
          const item = defaultBooleanField(data, "covenants.ffeReserve");
          if (item === "--") {
            return false;
          }

          return item;
        },
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.ffeReserve"
      }
    ]
  },
  ffeReserveAccount: {
    isVisible: data => shouldShowFfeReserveAccount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "FF&E Account",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => {
          const item = defaultBooleanField(data, "covenants.ffeReserveAccount");
          if (item === "--") {
            return false;
          }

          return item;
        },
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.ffeReserveAccount"
      }
    ]
  },
  capExReserves: {
    isVisible: data => shouldShowCapExReserves(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Cap Ex Reserves",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: data => defaultField(data, "covenants.capExReserves"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.capExReserves"
      }
    ]
  },
  capExReservesMisc: {
    isVisible: data => shouldShowCapExReservesMisc(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Cap Ex Reserves Misc",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.capExReservesMisc"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.capExReservesMisc"
      }
    ]
  },
  capExCompletionDeadline: {
    isVisible: data => shouldShowCapExCompletionDeadline(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Cap Ex COMPLETION DEADLINE",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.STRING_SUBTEXT_NESTED,
        mapNestedRows: data => deadlineMapNestedRows(data, "capExCompletionDeadline"),
        map: row => defaultField(row, "name"),
        Cell: tableSideColumnMapper,
        subtextMappers: [, { map: row => mapRefSubtext(row, "dueDate") }],
        colSpan: 2
      }
    ]
  },

  capExReservesDrawn: {
    isVisible: data => shouldShowCapExReservesDrawn(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Cap Ex Reserves Drawn",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: data => {
          // this value is saved as a string in the DB, so doing some checks and converting to a #
          const value = defaultField(data, "covenants.capExReservesDrawn");
          if (!value || value === "--" || Number.isNaN(Number(value))) {
            return value;
          }

          return Number(value);
        },
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.capExReservesDrawn"
      }
    ]
  },

  shortfallReserve: {
    isVisible: data => shouldShowShortfallReserve(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Debt Service/Interest Shortfall Reserve",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: data => defaultField(data, "covenants.shortfallReserve"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.shortfallReserve"
      }
    ]
  },

  shortfallReserveMisc: {
    isVisible: data => shouldShowShortfallReserveMisc(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Debt Service/Interest Shortfall Reserve Misc",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.shortfallReserveMisc"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.shortfallReserveMisc"
      }
    ]
  },

  operatingAccount: {
    isVisible: data => shouldShowOperatingAccount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Operating Account",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.operatingAccount"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.operatingAccount"
      }
    ]
  },

  interestRateCapReserve: {
    isVisible: data => shouldShowInterestRateCapReserve(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Interest Rate Cap Reserve",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: data => defaultField(data, "covenants.interestRateCapReserve"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.interestRateCapReserve"
      }
    ]
  },

  interestRateCapReserveMisc: {
    isVisible: data => shouldShowInterestRateCapReserveMisc(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Interest Rate Cap Reserve Misc",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.interestRateCapReserveMisc"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.interestRateCapReserveMisc"
      }
    ]
  },

  interestReserve: {
    isVisible: data => shouldShowInterestReserve(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Interest Reserve",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: data => defaultField(data, "covenants.interestReserve"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.interestReserve"
      }
    ]
  },

  interestReserveMisc: {
    isVisible: data => shouldShowInterestReserveMisc(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Interest Reserve Misc",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.interestReserveMisc"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.interestReserveMisc"
      }
    ]
  },

  minCashDeposit: {
    isVisible: data => shouldShowmInCashDeposit(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Min Cash Deposit",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: data => defaultField(data, "covenants.minCashDeposit"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.minCashDeposit"
      }
    ]
  },

  miscReserves: {
    isVisible: data => shouldShowmMiscReserves(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Reserves Misc",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.escrowAccountsMisc"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.escrowAccountsMisc"
      }
    ]
  }
};
